/**
 * localStorage Caching
 * Cache management with TTL tracking and staleness detection
 * Supports multi-region cache keys
 */

import { CACHE_CONFIG } from './config.js';
import { AppState, getCurrentRegionId, getCurrentBuoy } from './state.js';
import { getDisplayAge as sharedGetDisplayAge, CACHE_TTL, timestamp, ageInSeconds } from './time.js';
import { logger } from './logger.js';
import { DEFAULTS } from '../shared/defaults.mjs';

/**
 * Build a full cache key with region/station context
 * @param {string} baseKey - The base cache key (e.g., 'predictions')
 * @returns {string} Full cache key with region/station prefix
 */
function buildCacheKey(baseKey) {
    const regionId = getCurrentRegionId();
    const stationId = AppState.currentStationId || DEFAULTS.station;

    // Find the config for this key to determine scope
    const config = Object.values(CACHE_CONFIG).find(c => c.key === baseKey);

    if (config?.scope === 'buoy') {
        const buoyId = getCurrentBuoy();
        return `tide-${regionId}-${buoyId}-${baseKey}`;
    }

    return `tide-${regionId}-${stationId}-${baseKey}`;
}

/**
 * Check if a cache key matches the current context
 * Handles both old-style (sd-tide-*) and new-style (tide-region-station-*) keys
 * @param {string} cacheKey - The cache key to check
 * @param {object} cacheEntry - The cached data entry
 * @returns {boolean} Whether the cache is valid for current context
 */
function isCacheValidForContext(cacheKey, cacheEntry) {
    // New-style keys include region/station in the key itself
    // They're valid by definition since we build them from current context
    if (cacheKey.startsWith('tide-')) {
        return true;
    }

    // Old-style keys (sd-tide-*) - check stationId in the entry
    if (cacheEntry.stationId && cacheEntry.stationId !== AppState.currentStationId) {
        return false;
    }

    return true;
}

/**
 * Retrieve cached data if valid
 * @param {string} cacheKey - The cache key to look up (can be base key or full key)
 * @param {boolean} allowStale - Whether to return expired data
 * @returns {object|null} Cached data with metadata or null
 */
export function getCachedData(cacheKey, allowStale = false) {
    try {
        // If it's a base key (no prefix), build the full key
        const fullKey = cacheKey.startsWith('tide-') ? cacheKey : buildCacheKey(cacheKey);

        const cached = localStorage.getItem(fullKey);

        // Also try old-style key for backwards compatibility
        const legacyCached = !cached ? localStorage.getItem(`sd-tide-${cacheKey}`) : null;

        const cacheData = cached || legacyCached;
        if (!cacheData) return null;

        const cacheEntry = JSON.parse(cacheData);
        const { data, timestamp: cachedTimestamp } = cacheEntry;

        // Check if cache is valid for current context
        if (!isCacheValidForContext(fullKey, cacheEntry)) return null;

        // Find TTL config - try matching by key
        let ttl = CACHE_TTL.conditions; // Default to conditions TTL (5 minutes)
        const config = Object.values(CACHE_CONFIG).find(c =>
            fullKey.includes(c.key) || cacheKey === c.key
        );
        if (config?.ttl) {
            ttl = config.ttl;
        }

        const isExpired = timestamp() - cachedTimestamp > ttl;

        // Return null if expired and not allowing stale data
        if (isExpired && !allowStale) return null;

        const ageSeconds = ageInSeconds(cachedTimestamp);
        logger.log(`Cache hit for ${fullKey} (${ageSeconds}s old${isExpired ? ', stale' : ''})`);
        return { data, timestamp: cachedTimestamp, fromCache: true, stale: isExpired };
    } catch (e) {
        logger.warn('Cache read error:', e);
        return null;
    }
}

/**
 * Store data in cache with current timestamp
 * @param {string} cacheKey - The cache key (can be base key or full key)
 * @param {any} data - The data to cache
 */
export function setCachedData(cacheKey, data) {
    try {
        // If it's a base key (no prefix), build the full key
        const fullKey = cacheKey.startsWith('tide-') ? cacheKey : buildCacheKey(cacheKey);

        const cacheEntry = {
            data,
            timestamp: timestamp(),
            regionId: getCurrentRegionId(),
            stationId: AppState.currentStationId
        };
        localStorage.setItem(fullKey, JSON.stringify(cacheEntry));
    } catch (e) {
        logger.warn('Cache write error:', e);
    }
}

/**
 * Clear all cache entries for the current region/station
 */
export function clearCurrentCache() {
    const regionId = getCurrentRegionId();
    const stationId = AppState.currentStationId;
    const prefix = `tide-${regionId}-${stationId}`;

    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix)) {
            keysToRemove.push(key);
        }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    logger.log(`Cleared ${keysToRemove.length} cache entries for ${prefix}`);
}

/**
 * Clear all tide-related cache entries
 */
export function clearAllCache() {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('tide-') || key?.startsWith('sd-tide-')) {
            keysToRemove.push(key);
        }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    logger.log(`Cleared ${keysToRemove.length} total cache entries`);
}

/**
 * Get human-readable age and freshness class
 * @param {number} timestamp - Unix timestamp
 * @returns {object} { text: string, class: 'fresh'|'stale'|'old' }
 */
export function getDataAge(timestamp) {
    return sharedGetDisplayAge(timestamp);
}

// ===========================================
// Cache Helper Functions (DRY)
// ===========================================

/**
 * Get cached data and update timestamp tracker
 * Returns cached data with timestamp updated in the provided tracker object
 *
 * @param {string} cacheKey - Cache key (base key will be prefixed automatically)
 * @param {string} timestampKey - Key in the timestamps object to update
 * @param {Object} timestampStore - Object to store the timestamp in (e.g., dataTimestamps)
 * @returns {Object|null} Cached data or null
 */
export function getCachedWithTimestamp(cacheKey, timestampKey, timestampStore) {
    const cached = getCachedData(cacheKey);
    if (cached) {
        timestampStore[timestampKey] = cached.timestamp;
        return cached;
    }
    return null;
}

/**
 * Save data to cache and update timestamp tracker
 *
 * @param {string} cacheKey - Cache key
 * @param {any} data - Data to cache
 * @param {string} timestampKey - Key in the timestamps object to update
 * @param {Object} timestampStore - Object to store the timestamp in
 */
export function saveCacheWithTimestamp(cacheKey, data, timestampKey, timestampStore) {
    setCachedData(cacheKey, data);
    timestampStore[timestampKey] = timestamp();
}

/**
 * Get stale cache data (for error fallback)
 * Convenience wrapper for getCachedData(key, true)
 *
 * @param {string} cacheKey - Cache key
 * @returns {Object|null} Stale cached data or null
 */
export function getStaleCacheFallback(cacheKey) {
    return getCachedData(cacheKey, true);
}
